import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Progress } from "@/components/_atoms/progress";
import { getProgressBarColors } from "@/domain/inventory/lib/color-utils";

type UploadStatus = "uploading" | "success" | "error";

const FILE_NAME_TONE: Record<UploadStatus, string> = {
  uploading: "text-slate-700",
  success: "text-green-700",
  error: "text-red-700",
};

interface UploadStatusRowProps {
  fileName: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  onClear: () => void;
}

export const UploadStatusRow = ({ fileName, status, progress, error, onClear }: UploadStatusRowProps) => {
  const { t } = useTranslation("common");
  const clearLabel = status === "uploading" ? t("editor.cancelUpload") : t("editor.removeFile");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className={`truncate text-sm font-medium ${FILE_NAME_TONE[status]}`}>{fileName}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground"
          aria-label={clearLabel}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {status === "uploading" && (
        <>
          <Progress
            value={progress}
            style={{ background: getProgressBarColors().background }}
            indicatorColor={getProgressBarColors().indicator}
            aria-label={t("editor.uploadingProgress", { progress })}
          />
          <p className="text-xs text-muted-foreground">{t("editor.uploadingProgress", { progress })}</p>
        </>
      )}

      {status === "success" && <p className="text-xs text-green-600">{t("editor.uploadSuccess")}</p>}

      {status === "error" && <p className="text-xs text-red-600">{t("editor.uploadFailed", { error })}</p>}
    </div>
  );
};
